import { createContext, JSX, useContext, useReducer, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { UserContext } from "@app/user/UserContext";
import { useLocation, useParams } from "react-router-dom";
import { EntityTypeEnum, NetworkIntegrityJobStatus, ProjectRunConfigurationType, type RegionSetStatusType } from "@app/projects/enumerators";
import { NetworkIntegrityFilterActions, networkIntegrityFilterInitialState, networkIntegrityFilterReducer } from "@app/projects/network-assessment/network-integrity/state-management/networkIntegrityFilterStateManagement";
import { NetworkIntegrityJobActions, networkIntegrityJobInitialState, networkIntegrityJobReducer } from "@app/projects/network-assessment/network-integrity/state-management/networkIntegrityJobStateManagement";
import { NetworkIntegrityRouteState } from "@app/projects/network-assessment/network-integrity/models";
import { NetworkIntegrityActionStatus, NetworkIntegrityDisplayMode, NetworkIntegrityMembership } from "@app/projects/network-assessment/network-integrity/filters/networkIntegrityFilterConstants";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import Project from "@app/projects/project";
import ProjectRunConfiguration from "@app/projects/projectRunConfiguration";
import ProjectService from "@app/projects/projectService";

export interface NetworkIntegrityContextProps {
  actionStatus?: NetworkIntegrityActionStatus;
  canResubmit?: boolean;
  filtersChanged: React.Dispatch<NetworkIntegrityFilterActions>;
  isAdequacyImpactExpected?: boolean;
  isAdequacyImpactProcessing?: boolean;
  isJobStatusLoading?: boolean;
  isLoading?: boolean;
  isOpportunityServiceArea?: boolean;
  jobStatus?: NetworkIntegrityJobStatus;
  jobStatusChanged: React.Dispatch<NetworkIntegrityJobActions>;
  membership?: NetworkIntegrityMembership;
  npiType?: EntityTypeEnum | null;
  project?: Project;
  projectRunConfiguration?: ProjectRunConfiguration;
  projectRunConfigurationDualType?: ProjectRunConfigurationType;
  projectRunConfigurationId?: string;
  providerType?: string;
  regionSetIds?: number[];
  specialtyCodes?: string[];
  displayMode: NetworkIntegrityDisplayMode;
}

export const NetworkIntegrityContext = createContext<Partial<NetworkIntegrityContextProps>>({});

// Persists the last applied provider type across unmount/remount cycles (tab switches).
// Module-level so it survives the context provider being destroyed and recreated.
let _lastAppliedProviderType: string | undefined;
export const getLastAppliedProviderType = (): string | undefined => _lastAppliedProviderType;
export const setLastAppliedProviderType = (value: string | undefined): void => { _lastAppliedProviderType = value; };

let _lastAppliedDisplayMode: NetworkIntegrityDisplayMode | undefined;
export const getLastAppliedDisplayMode = (): NetworkIntegrityDisplayMode | undefined => _lastAppliedDisplayMode;
export const setLastAppliedDisplayMode = (value: NetworkIntegrityDisplayMode): void => { _lastAppliedDisplayMode = value; };

// Persists the last applied County Status pill selection across unmount/remount cycles so
// chip state survives navigation between the dashboard and detail pages — see WI 189072.
// undefined means "use NI_DEFAULT_STATUS_FILTERS".
let _lastAppliedStatusFilters: RegionSetStatusType[] | undefined;
export const getLastAppliedStatusFilters = (): RegionSetStatusType[] | undefined => _lastAppliedStatusFilters;
export const setLastAppliedStatusFilters = (value: RegionSetStatusType[] | undefined): void => { _lastAppliedStatusFilters = value; };

let _lastAppliedMembership: NetworkIntegrityMembership | undefined;
export const getLastAppliedMembership = (): NetworkIntegrityMembership | undefined => _lastAppliedMembership;
export const setLastAppliedMembership = (value: NetworkIntegrityMembership | undefined): void => { _lastAppliedMembership = value; };

export const NetworkIntegrityContextProvider = ({ children }: { children: React.ReactNode }): JSX.Element => {
  const location = useLocation<NetworkIntegrityRouteState>();
  const { projectId, providerType: urlProviderType } = useParams<{ projectId: string; providerType?: string }>();
  const initialProviderType = urlProviderType ?? location.state?.initialProviderType ?? _lastAppliedProviderType;
  const initialActionStatus = location.state?.initialActionStatus;
  const initialDisplayMode = _lastAppliedDisplayMode;
  const [filterState, filtersChanged] = useReducer(networkIntegrityFilterReducer, networkIntegrityFilterInitialState(initialProviderType, initialActionStatus, initialDisplayMode, _lastAppliedMembership, location.state?.initialNpiType ?? null));
  const [jobState, jobStatusChanged] = useReducer(networkIntegrityJobReducer, networkIntegrityJobInitialState());
  const projectService = useRef(new ProjectService()).current;
  const user = useContext(UserContext);
  const { data: project, error: projectError } = useQuery<Project, JQuery.jqXHR>({
    queryFn: ({ signal }) => projectService.getById(Number(projectId), true, false, undefined, false, signal),
    queryKey: ["project", user?.userId, projectId],
    refetchOnMount: "always",
    staleTime: 5 * 60 * 1000,
  });

  if (projectError) {
    // Thrown shape matches ErrorBoundary's getDerivedStateFromError contract; status 403 surfaces as Forbidden.
    throw { status: projectError.status, statusText: projectError.statusText, traceId: projectError.responseJSON?.traceId };
  }

  if (!project) {
    return <LoadingSpinner />;
  }

  return (
    <NetworkIntegrityContext.Provider
      value={{
        actionStatus: filterState.actionStatus,
        canResubmit: jobState.canResubmit,
        displayMode: filterState.displayMode,
        filtersChanged,
        isAdequacyImpactExpected: jobState.isAdequacyImpactExpected,
        isAdequacyImpactProcessing: jobState.isAdequacyImpactProcessing,
        isJobStatusLoading: jobState.isJobLoading,
        isLoading: filterState.isLoading,
        isOpportunityServiceArea: jobState.isOpportunityServiceArea,
        jobStatus: jobState.jobStatus,
        jobStatusChanged,
        membership: filterState.membership,
        npiType: filterState.npiType,
        project,
        projectRunConfiguration: filterState.projectRunConfiguration,
        projectRunConfigurationDualType: filterState.projectRunConfigurationDualType,
        projectRunConfigurationId: filterState.projectRunConfigurationId,
        providerType: filterState.providerType,
        regionSetIds: filterState.regionSetIds,
        specialtyCodes: filterState.specialtyCodes,
      }}
    >
      {children}
    </NetworkIntegrityContext.Provider>
  );
}